// Readable extension to the supplied compiled frontend. No build step required.
export function createRecentPosts(React) {
  const h = React.createElement;
  const plainText = (html) => {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    doc.querySelectorAll("script, style").forEach((node) => node.remove());
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  };

  return function RecentPosts() {
    const [posts, setPosts] = React.useState([]);
    const [status, setStatus] = React.useState("loading");
    const [attempt, setAttempt] = React.useState(0);
    const [expanded, setExpanded] = React.useState(false);
    const [visible, setVisible] = React.useState(() => location.hash === '#blog');
    const section = React.useRef(null);
    React.useEffect(() => {
      if (visible) return;
      if (!('IntersectionObserver' in window)) { setVisible(true); return; }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
      }, {rootMargin: '400px'});
      if (section.current) observer.observe(section.current);
      return () => observer.disconnect();
    }, [visible]);

    React.useEffect(() => {
      const refresh = () => { if (!document.hidden) setAttempt(value => value + 1); };
      document.addEventListener("visibilitychange", refresh);
      const timer = setInterval(() => {
        if (!document.hidden) setAttempt(value => value + 1);
      }, 60000);
      return () => { clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
    }, []);

    React.useEffect(() => {
      if (window.location.hash !== "#blog") return;
      const frame = requestAnimationFrame(() => document.getElementById("blog")?.scrollIntoView());
      return () => cancelAnimationFrame(frame);
    }, []);

    React.useEffect(() => {
      if (!visible) return;
      const controller = new AbortController();
      let active = true;
      const timeout = setTimeout(() => controller.abort(), 12000);
      setStatus(previous => previous === "ready" ? previous : "loading");
      const loadPosts = async () => {
        const query = 'per_page=10&orderby=date&order=desc&status=publish&_embed=wp:featuredmedia,wp:term';
        for (const endpoint of ['/recent-posts.php','/blog/?rest_route=/wp/v2/posts&'+query,'/blog/wp-json/wp/v2/posts?'+query]) {
          try {
            const response = await fetch(endpoint,{signal:controller.signal,credentials:'omit',cache:'no-store'});
            if (!response.ok) continue;
            const data = await response.json();
            if (Array.isArray(data)) return data;
          } catch (error) { if (controller.signal.aborted) throw error; }
        }
        throw new Error('Blog unavailable');
      };
      loadPosts()
        .then((data) => {
          if (!Array.isArray(data)) throw new Error("Invalid blog response");
          const recent = data.filter((post) => {
            try {
              const url = new URL(post.link);
              return url.protocol === "https:" &&
                ["www.ghostswap.app", "ghostswap.app"].includes(url.hostname) &&
                Number.isFinite(Date.parse(post.date));
            } catch { return false; }
          }).sort((a, b) => Date.parse(b.date) - Date.parse(a.date)).slice(0, 10);
          if (active) { setPosts(recent); setStatus("ready"); }
        })
        .catch(() => { if (active) setStatus(previous => previous === 'ready' ? previous : 'error'); })
        .finally(() => clearTimeout(timeout));
      return () => { active = false; clearTimeout(timeout); controller.abort(); };
    }, [attempt, visible]);

    const renderPost = (post, featured) => {
      const title = plainText(post.title?.rendered) || "Untitled post";
      const excerpt = plainText(post.excerpt?.rendered);
      const media = post._embedded?.["wp:featuredmedia"]?.[0];
      const category = post._embedded?.["wp:term"]?.flat().find(term => term.taxonomy === "category");
      const words = plainText(post.content?.rendered).split(/\s+/).filter(Boolean).length;
      let image;
      try {
        const url = new URL(media?.source_url);
        if (url.protocol === "https:") image = url.href;
      } catch { /* Render the branded fallback when no featured image exists. */ }
      return h("a", { key: post.id, href: post.link, className: featured ? "recent-posts-feature" : "recent-posts-card" },
        h("div", { className: "recent-posts-image" },
          h("img", { src: image || "/logo.png", alt: plainText(media?.alt_text) || "", loading: "lazy", onError: event => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/logo.png";
          } })),
        h("div", { className: "recent-posts-body" },
          h("div", { className: "recent-posts-meta" },
            category ? h("span", { className: "recent-posts-category" }, plainText(category.name)) : null,
            h("time", { dateTime: post.date }, new Date(post.date).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" })),
            words ? h("span", null, "·  " + Math.max(1, Math.ceil(words / 220)) + " min read") : null),
          h("h3", null, title),
          h("p", { className: "recent-posts-excerpt" }, excerpt.length > 220 ? excerpt.slice(0, 217).trimEnd() + "…" : excerpt),
          h("span", { className: "recent-posts-link", "aria-hidden": "true" }, "Read article", h("span", null, " →"))));
    };

    return h("section", { ref: section, id: "blog", className: "recent-posts", "aria-labelledby": "recent-posts-title" },
      h("header", { className: "recent-posts-heading" },
        h("p", { className: "recent-posts-eyebrow" }, h("a", { href: "/blog", className: "recent-posts-blog-link" }, "From the blog")),
        h("h2", { id: "recent-posts-title" }, "Latest post")),
      h("div", { role: "status", "aria-live": "polite" },
        status === "loading" ? h("p", { className: "recent-posts-message" }, "Loading latest post…") : null,
        status === "error" ? h("div", { className: "recent-posts-message" },
          h("p", null, "Recent posts couldn’t be loaded. Please try again."),
          h("button", { type: "button", className: "recent-posts-link", onClick: () => setAttempt(value => value + 1) }, "Try again")) : null,
        status === "ready" && posts.length === 0 ? h("p", { className: "recent-posts-message" }, "No blog posts yet. Check back soon.") : null),
      status === "ready" && posts.length > 0 ? renderPost(posts[0], true) : null,
      status === "ready" && posts.length > 1 ? h(React.Fragment, null,
        h("div", { className: "recent-posts-more" }, h("button", {
          type: "button", "aria-expanded": expanded, "aria-controls": "older-blog-posts",
          onClick: () => setExpanded(value => !value), className: "recent-posts-toggle"
        }, expanded ? "Fewer posts ↑" : "More posts ↓")),
        h("div", { id: "older-blog-posts", hidden: !expanded },
          h("div", { className: "recent-posts-grid" }, expanded ? posts.slice(1).map(post => renderPost(post, false)) : null),
          h("div", { className: "recent-posts-more" }, h("a", { href: "https://www.ghostswap.app/blog/", className: "recent-posts-link" }, "View all posts →")))) : null);
  };
}
